import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useRef } from 'react';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { formatDMY } from '@application/utilities/date-time-formater';
import { FieldType } from '@application/enums/field-types';
import { Box, Button, useMediaQuery, useTheme, Typography, CircularProgress, alpha } from '@mui/material';
import * as XLSX from 'xlsx';
import { PdfIcon } from '@ui/assets/custom-icons/pdf-icon';
import { RegistrationType } from '@application/enums/registration-type';
/**
 * ResponsesTab Component
 *
 * This component displays event responses in a data grid with the following features:
 * - Dynamic columns based on API response
 * - Automatic field type detection and formatting based on field types
 * - Date fields (type '4') are formatted as DD/MM/YYYY
 * - Time fields (type '5') are formatted as HH:MM AM/PM
 * - Toggle fields (type '12') display "Yes" or "No" instead of "true" or "false"
 * - All formatting is applied to both grid display and Excel export
 */
import { GridEdit, Trash, Archive, Export, AddCircle } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { centered } from '@ui/style/style';
import { Icon } from '@iconify/react';
import { StyledMenu } from '@ui/components/common/Menu';
import OutlinedIconButton from '@ui/components/common/IconButton';
import AdditionalAttendeesCell from './AdditionalAttendeesCell';
import EditColumnDialog from './EditColumnDialog';
import EditUserResponseDialog from './EditUserResponseDialog';
import AddUserResponseDialog from './AddUserResponseDialog';
import AddFamilyResponseDialog from './AddFamilyResponseDialog';
import EditFamilyResponseDialog from './EditFamilyResponseDialog';
import FamilyResponsesGrid from './FamilyResponsesGrid';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAllResponsesGridLabelsByEventIdAsync, getAllActiveResponsesByEventIdGroupedByRegistrationAsync, getAllArchivedResponsesByEventIdGroupedByRegistrationAsync, getStepsFieldsResponsesByRegistrationIdAsync, archiveResponsesAsync, unarchiveResponsesAsync, deleteResponsesAsync, exportResponsesToExcelAsync } from '@application/features/vdkEventResponses/vdkEventResponsesThunk';
import { getEventLanguagesByEventIdAsync } from '@application/features/languages/languagesThunk';
import { useParams } from 'react-router-dom';
import { hasPermission } from '@application/helpers/permission-helper';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import { UnarchiveOutlined } from '@mui/icons-material';
import { Iconly } from 'react-iconly';
import { EventTypeEnum } from '@application/enums/event-type-enum';
const ACTIONS_COLUMN_WIDTH = 120;
const pinnedColumnSize = (width) => ({
    width: `${width}px !important`,
    minWidth: `${width}px !important`,
    maxWidth: `${width}px !important`,
    flex: `0 0 ${width}px !important`,
    boxSizing: 'border-box'
});
const getOpaqueColorMix = (baseColor, mixColor, mixRatio) => `color-mix(in srgb, ${mixColor} ${Math.round(mixRatio * 100)}%, ${baseColor})`;
const pinnedHeaderChildrenBg = (backgroundColor) => ({
    '& .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent': {
        backgroundColor
    }
});
export const ResponsesTab = ({ onAttendeesChanged }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const { eventId, type } = useParams();
    const tabTypeNumber = Number(type);
    const { allResponsesGridLabelsByEventId, allResponsesByEventId, allArchivedResponsesByEventId, status, stepsFieldsResponses } = useAppSelector((state) => state.vdkEventResponse);
    const { languages: appLanguages, eventLanguages } = useAppSelector((state) => state.languages);
    const primaryEventLanguageCode = ((eventLanguages ?? []).find((l) => l.isPrimary) ?? (eventLanguages ?? [])[0])?.code?.toLowerCase() ?? 'en';
    // Get user permissions from auth store
    const loggedInUserPermissions = useAppSelector((state) => state.auth.currentUser?.permissions || []);
    const canEditEvent = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    const canDeleteEvent = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Delete');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [sortModel, setSortModel] = useState([
        { field: 'registrationDate', sort: 'desc' }
    ]);
    // Add state for menu anchor
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState(null);
    const handleOptionsMenuOpen = (e) => setOptionsMenuAnchorEl(e.currentTarget);
    const handleOptionsMenuClose = () => setOptionsMenuAnchorEl(null);
    // Collaborator role check
    const currentUserRoles = useAppSelector((state) => state.auth.currentUser?.role);
    const isCollaborator = Array.isArray(currentUserRoles) &&
        (currentUserRoles.includes('Collaborator') || currentUserRoles.includes('TransportManager'));
    // Add state for edit column dialog
    const [editColumnDialogOpen, setEditColumnDialogOpen] = useState(false);
    const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
    const [addAttendeeDialogOpen, setAddAttendeeDialogOpen] = useState(false);
    // Removed familyMockRows and mock data state
    const [selectedUserData, setSelectedUserData] = useState(null);
    const [isEditDialogLoading, setIsEditDialogLoading] = useState(false);
    const familyFestRefetchAttendeesRef = useRef(null);
    const [selectedTab, setSelectedTab] = useState('active');
    // Add state for confirmation dialogs
    const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [actionTargetId, setActionTargetId] = useState(null);
    // Add state for bulk operation confirmation dialogs
    const [bulkArchiveConfirmationOpen, setBulkArchiveConfirmationOpen] = useState(false);
    const [bulkUnarchiveConfirmationOpen, setBulkUnarchiveConfirmationOpen] = useState(false);
    const [bulkDeleteConfirmationOpen, setBulkDeleteConfirmationOpen] = useState(false);
    const [selectedRowsForBulkOperation, setSelectedRowsForBulkOperation] = useState([]);
    const VdkEventDetails = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const IsFamilyFest = VdkEventDetails?.type === EventTypeEnum.Family;
    // Ensure application languages are available so we can derive a default
    // useEffect(() => {
    //   if (!appLanguages || appLanguages.length === 0) {
    //     dispatch(getAllLanguagesAsync());
    //   }
    // }, [appLanguages, dispatch]);
    // const defaultAppLanguage = useMemo<LanguageDto>(() => {
    //   if (appLanguages && appLanguages.length > 0) {
    //     const normalizedCurrent = (i18n.language || 'en').toLowerCase();
    //     return (
    //       appLanguages.find((lang) => lang.isPrimary) ??
    //       appLanguages.find((lang) => lang.code?.toLowerCase() === normalizedCurrent) ??
    //       appLanguages[0]
    //     );
    //   }
    //   // Fallback when languages are not loaded yet
    //   return {
    //     id: 'default-language',
    //     name: 'English',
    //     code: 'en',
    //     locale: 'en',
    //     region: 'US',
    //     flagUrl: '/assets/images/flags/us-flag.svg',
    //     isPrimary: true,
    //     createdDate: '',
    //     createdBy: '',
    //     isDeleted: false
    //   };
    // }, [appLanguages, i18n.language]);
    const buildSortParams = (model) => {
        if (model.length === 0)
            return { sortBy: 'registrationDate', sortDirection: 'desc' };
        return { sortBy: model[0].field, sortDirection: model[0].sort ?? 'desc' };
    };
    // Refresh grid data after column updates
    const refreshGridData = () => {
        if (eventId) {
            dispatch(getAllResponsesGridLabelsByEventIdAsync(eventId));
            const request = {
                eventId,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                ...buildSortParams(sortModel)
            };
            if (selectedTab === 'active') {
                dispatch(getAllActiveResponsesByEventIdGroupedByRegistrationAsync(request));
            }
            else {
                dispatch(getAllArchivedResponsesByEventIdGroupedByRegistrationAsync(request));
            }
        }
    };
    // Refresh both active and archived data (used after archive/unarchive operations)
    const refreshAllData = () => {
        if (eventId) {
            const request = {
                eventId,
                pageNumber: 1,
                pageSize: paginationModel.pageSize,
                ...buildSortParams(sortModel)
            };
            dispatch(getAllActiveResponsesByEventIdGroupedByRegistrationAsync(request));
            dispatch(getAllArchivedResponsesByEventIdGroupedByRegistrationAsync(request));
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }
    };
    // Fetch grid labels on component mount only
    useEffect(() => {
        if (eventId) {
            dispatch(getAllResponsesGridLabelsByEventIdAsync(eventId));
            dispatch(getEventLanguagesByEventIdAsync(eventId));
        }
    }, [dispatch, eventId]);
    // Fetch responses data for current tab and pagination
    useEffect(() => {
        if (eventId) {
            const request = {
                eventId,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                ...buildSortParams(sortModel)
            };
            if (selectedTab === 'active') {
                dispatch(getAllActiveResponsesByEventIdGroupedByRegistrationAsync(request));
            }
            else {
                dispatch(getAllArchivedResponsesByEventIdGroupedByRegistrationAsync(request));
            }
        }
    }, [dispatch, eventId, paginationModel.page, paginationModel.pageSize, selectedTab, sortModel]);
    // Handle tab changes - reset pagination to first page
    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, [selectedTab]);
    // Load field types when we have responses data. Re-fetches whenever the grid's own field list
    // (allResponsesGridLabelsByEventId) contains a field id this cache doesn't know about yet - e.g.
    // an admin adds a second Additional Attendees field to the form in the builder while this tab is
    // already mounted, or was mounted before that field existed. Without this, stepsFieldsResponses
    // stays permanently stale for that field, getFieldType() returns null for it forever, and its grid
    // cell falls back to rendering the raw merged "Person 1: ...\nPerson 2: ..." string instead of the
    // AdditionalAttendeesCell chip+popup - the exact bug this refetch trigger is closing.
    // Guarded by a ref (not just re-running on every stepsFieldsResponses change) so a field id that's
    // genuinely gone missing from the backend can't cause a refetch loop.
    const fetchedFieldTypesForGridLabelsRef = useRef(null);
    useEffect(() => {
        if (!eventId ||
            !allResponsesByEventId?.items ||
            allResponsesByEventId.items.length === 0 ||
            !allResponsesGridLabelsByEventId ||
            fetchedFieldTypesForGridLabelsRef.current === allResponsesGridLabelsByEventId) {
            return;
        }
        const knownFieldIds = new Set((stepsFieldsResponses || []).flatMap((step) => (step.formFields || []).map((f) => f.id)));
        const hasMissingFieldTypes = allResponsesGridLabelsByEventId.some((label) => !knownFieldIds.has(label.id));
        if (!stepsFieldsResponses || hasMissingFieldTypes) {
            fetchedFieldTypesForGridLabelsRef.current = allResponsesGridLabelsByEventId;
            const firstRegistrationId = allResponsesByEventId.items[0].registrationId;
            dispatch(getStepsFieldsResponsesByRegistrationIdAsync({
                registrationId: firstRegistrationId,
                eventId: eventId
            }));
        }
    }, [
        eventId,
        allResponsesByEventId?.items,
        allResponsesGridLabelsByEventId,
        stepsFieldsResponses,
        dispatch
    ]);
    // Reset edit dialog loading state when data is loaded
    useEffect(() => {
        if (stepsFieldsResponses && isEditDialogLoading) {
            setIsEditDialogLoading(false);
        }
    }, [stepsFieldsResponses, isEditDialogLoading]);
    // Reads field type from the same call that defines the grid's own column list
    // (allResponsesGridLabelsByEventId), instead of the separately-fetched/cached stepsFieldsResponses
    // - that used to be able to fall out of sync with the actual current column set whenever the form
    // structure changed (e.g. a new field added), silently breaking type-dependent cell rendering
    // (like the Additional Attendees chip+popup) with no way for it to ever catch up on its own.
    const getFieldType = (fieldId) => {
        const label = allResponsesGridLabelsByEventId?.find((l) => l.id === fieldId);
        return label?.fieldType ?? null;
    };
    // Fallback function to detect field type based on response values
    const detectFieldTypeFromValue = (value) => {
        if (value === null || value === undefined || value === '')
            return null;
        const strValue = value.toString().trim();
        // Check if it's a toggle value
        if (strValue.toLowerCase() === 'true' || strValue.toLowerCase() === 'false') {
            return FieldType.TOGGLE;
        }
        // Check if it's an ISO date string (like "2025-08-28T19:00:00.000Z")
        if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    // Parse the time part to see if it has meaningful time data
                    const timePart = value.split('T')[1];
                    // If it's exactly midnight (00:00:00.000Z), it's likely a date field
                    // If it has any other time, it's likely a time field
                    if (timePart === '00:00:00.000Z') {
                        return FieldType.DATE;
                    }
                    else {
                        // For now, let the label-based detection handle this
                        // as it's more reliable for distinguishing date vs time fields
                        return null;
                    }
                }
            }
            catch {
                // Not a valid ISO date
            }
        }
        // Check if it's a date string in other formats (like "Thu Aug 28 2025 00:00:00 GMT+0500")
        if (typeof value === 'string' && (value.includes('GMT') || value.includes('Standard Time'))) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return FieldType.DATE;
                }
            }
            catch {
                // Not a valid date
            }
        }
        return null;
    };
    // Function to determine if a field should be treated as a specific type based on column label
    const getFieldTypeFromLabel = (label) => {
        const lowerLabel = label.toLowerCase();
        // Check for toggle indicators in the label
        if (lowerLabel.includes('toggle') ||
            lowerLabel.includes('yes/no') ||
            lowerLabel.includes('y/n')) {
            return FieldType.TOGGLE;
        }
        // Check for date indicators in the label (must be more specific to avoid conflicts)
        if (lowerLabel.includes('date') &&
            !lowerLabel.includes('time') &&
            !lowerLabel.includes('datetime')) {
            return FieldType.DATE;
        }
        // Check for time indicators in the label
        if (lowerLabel.includes('time') && !lowerLabel.includes('date')) {
            return FieldType.TIME;
        }
        return null;
    };
    // Helper function to check if a field should be treated as a date
    const isDateField = (fieldId) => {
        const fieldType = getFieldType(fieldId);
        return fieldType === FieldType.DATE;
    };
    // Helper function to check if a field should be treated as a time
    const isTimeField = (fieldId) => {
        const fieldType = getFieldType(fieldId);
        return fieldType === FieldType.TIME;
    };
    // Helper function to check if a field should be treated as a toggle
    const isToggleField = (fieldId) => {
        const fieldType = getFieldType(fieldId);
        return fieldType === FieldType.TOGGLE;
    };
    // Helper function to get the undertaking field ID
    const getUndertakingFieldId = () => {
        if (!stepsFieldsResponses)
            return null;
        for (const step of stepsFieldsResponses) {
            const undertakingField = step.formFields?.find((f) => f.fieldType === FieldType.UNDERTAKING);
            if (undertakingField) {
                return undertakingField.id;
            }
        }
        return null;
    };
    // Helper function to find signed undertaking document (type 17)
    const findSignedUndertakingDocument = (responses) => {
        const undertakingFieldId = getUndertakingFieldId();
        if (!undertakingFieldId)
            return null;
        // Find the undertaking field response
        const undertakingResponse = responses.find((r) => r.fieldId === undertakingFieldId);
        if (!undertakingResponse || !undertakingResponse.registrationDocuments) {
            return null;
        }
        // Find the document with type 17 (signedUndertaking)
        const signedUndertakingDoc = undertakingResponse.registrationDocuments.find((doc) => doc.type === 17);
        if (!signedUndertakingDoc || !signedUndertakingDoc.name || !signedUndertakingDoc.url) {
            return null;
        }
        return {
            name: undertakingResponse.registrationType === RegistrationType.Self
                ? signedUndertakingDoc.name
                : '-',
            url: signedUndertakingDoc.url
        };
    };
    // Handler to download signed undertaking document
    const handleDownloadSignedUndertaking = (url, fileName) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    // Custom time formatter that returns 12-hour format (03:15 PM)
    const formatTime12Hour = (timeString) => {
        if (!timeString || timeString === '')
            return '';
        try {
            // First, check if it's already a time string (contains : but not /)
            if (timeString.includes(':') && !timeString.includes('/')) {
                // It's a time string like "04:20 PM" or "16:20"
                const timeParts = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                if (timeParts) {
                    let hours = parseInt(timeParts[1]);
                    const minutes = parseInt(timeParts[2]);
                    const isPM = timeParts[3] && timeParts[3].toUpperCase() === 'PM';
                    // If it's 12-hour format with AM/PM, use as is
                    if (timeParts[3]) {
                        return timeString; // Already in correct format
                    }
                    // If it's 24-hour format, convert to 12-hour
                    if (hours === 0) {
                        hours = 12;
                    }
                    else if (hours > 12) {
                        hours = hours - 12;
                    }
                    const ampm = parseInt(timeParts[1]) >= 12 ? 'PM' : 'AM';
                    return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
                }
            }
            // If it contains both / and :, it's a date-time string
            if (timeString.includes('/') && timeString.includes(':')) {
                const date = new Date(timeString);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    });
                }
            }
            // If it's just a date string (contains / but not :), don't format as time
            if (timeString.includes('/') && !timeString.includes(':')) {
                return ''; // Don't show date in time field
            }
            // Try generic date parsing as fallback
            const date = new Date(timeString);
            if (!isNaN(date.getTime())) {
                return date.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });
            }
        }
        catch {
            // If parsing fails, return empty string for consistency
        }
        return '';
    };
    // Custom toggle formatter that returns "Yes" or "No" instead of "true" or "false"
    const formatToggleValue = (toggleValue) => {
        // Always return "No" for undefined, null, or empty values
        if (toggleValue === undefined || toggleValue === null || toggleValue === '')
            return 'No';
        // Handle both string and boolean values
        if (typeof toggleValue === 'boolean') {
            return toggleValue ? 'Yes' : 'No';
        }
        // Handle string values
        const value = toggleValue.toString().toLowerCase().trim();
        if (value === 'true' || value === '1' || value === 'yes') {
            return 'Yes';
        }
        else if (value === 'false' || value === '0' || value === 'no') {
            return 'No';
        }
        // Default to "No" for any other value (including whitespace-only strings)
        return 'No';
    };
    // Create dynamic columns based on API response
    const createDynamicColumns = () => {
        if (!allResponsesGridLabelsByEventId || allResponsesGridLabelsByEventId.length === 0) {
            return []; // Return empty array if no API data
        }
        const dynamicColumns = allResponsesGridLabelsByEventId
            .filter((label) => label.isFieldInResponsesGrid)
            .sort((a, b) => a.fieldSortOrderInResponseGrid - b.fieldSortOrderInResponseGrid)
            .map((label) => {
            // Check if this field should be treated as a date, time, or toggle
            const isDate = isDateField(label.id);
            const isTime = isTimeField(label.id);
            const isToggle = isToggleField(label.id);
            return {
                field: label.id,
                headerName: label.label,
                minWidth: 120,
                flex: 1,
                type: isDate ? 'date' : 'string',
                disableColumnMenu: isDate,
                disableColumnFilter: isDate,
                valueFormatter: (params) => {
                    // Safety check for params
                    if (!params)
                        return '';
                    // Check field type dynamically inside the formatter
                    const fieldId = params.field || label.id;
                    let fieldType = getFieldType(fieldId);
                    let detectionMethod = 'API';
                    // If field type is not available from API, try to detect from value
                    if (!fieldType) {
                        fieldType = detectFieldTypeFromValue(params.value);
                        detectionMethod = 'Value';
                    }
                    // If still no field type, try to detect from column label
                    if (!fieldType) {
                        fieldType = getFieldTypeFromLabel(label.label);
                        detectionMethod = 'Label';
                    }
                    const isToggleField = fieldType === FieldType.TOGGLE;
                    const isTimeField = fieldType === FieldType.TIME;
                    const isDateField = fieldType === FieldType.DATE;
                    // Always apply formatters based on field type, regardless of value
                    if (isToggleField) {
                        const result = formatToggleValue(params.value);
                        return result;
                    }
                    if (isTimeField) {
                        const result = formatTime12Hour(params.value);
                        return result;
                    }
                    if (isDateField) {
                        const formatted = formatDMY(params.value);
                        const result = formatted === 'Invalid Date' ? '' : formatted;
                        return result;
                    }
                    // For other field types, return the value as is
                    return params.value || '';
                },
                renderCell: (params) => {
                    // Safety check for params
                    if (!params)
                        return '';
                    // Check field type dynamically inside the renderCell
                    const fieldId = params.field || label.id;
                    let fieldType = getFieldType(fieldId);
                    let detectionMethod = 'API';
                    // If field type is not available from API, try to detect from value
                    if (!fieldType) {
                        fieldType = detectFieldTypeFromValue(params.value);
                        detectionMethod = 'Value';
                    }
                    // If still no field type, try to detect from column label
                    if (!fieldType) {
                        fieldType = getFieldTypeFromLabel(label.label);
                        detectionMethod = 'Label';
                    }
                    if (fieldType === FieldType.ADDITIONAL_ATTENDEES) {
                        return _jsx(AdditionalAttendeesCell, { value: params.value });
                    }
                    const isToggleField = fieldType === FieldType.TOGGLE;
                    const isTimeField = fieldType === FieldType.TIME;
                    const isDateField = fieldType === FieldType.DATE;
                    let formattedValue = '';
                    // Always apply formatters based on field type, regardless of value
                    if (isToggleField) {
                        formattedValue = formatToggleValue(params.value);
                    }
                    else if (isTimeField) {
                        formattedValue = formatTime12Hour(params.value);
                    }
                    else if (isDateField) {
                        const formatted = formatDMY(params.value);
                        formattedValue = formatted === 'Invalid Date' ? '' : formatted;
                    }
                    else {
                        formattedValue = params.value || '';
                    }
                    // // Always apply formatters based on field type, regardless of value
                    // if (isToggleField) {
                    //   const result = formatToggleValue(params.value);
                    //   return result;
                    // }
                    // if (isTimeField) {
                    //   const result = formatTime12Hour(params.value);
                    //   return result;
                    // }
                    // if (isDateField) {
                    //   const formatted = formatDMY(params.value);
                    //   const result = formatted === 'Invalid Date' ? '' : formatted;
                    //   return result;
                    // }
                    // // For other field types, return the value as is
                    // return params.value || '';
                    return (_jsx("span", { title: formattedValue, style: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }, children: formattedValue }));
                }
            };
        });
        // Additional Attendees should always be the last question column, regardless of its configured
        // sort order - it's a rolled-up summary of several people, not a single answer like the rest.
        dynamicColumns.sort((a, b) => {
            const aIsAdditionalAttendees = getFieldType(a.field) === FieldType.ADDITIONAL_ATTENDEES ? 1 : 0;
            const bIsAdditionalAttendees = getFieldType(b.field) === FieldType.ADDITIONAL_ATTENDEES ? 1 : 0;
            return aIsAdditionalAttendees - bIsAdditionalAttendees;
        });
        // Add responded date column if any responses exist
        const dataSource = selectedTab === 'active' ? allResponsesByEventId : allArchivedResponsesByEventId;
        const hasResponses = dataSource?.items && dataSource.items.length > 0;
        if (hasResponses) {
            // Insert responded date column after dynamic columns but before signed undertaking
            dynamicColumns.push({
                field: 'respondedDate',
                headerName: t('events.eventDetails.responsesTab.respondedDate', 'Responded Date') || 'Responded Date',
                minWidth: 150,
                flex: 1,
                type: 'date',
                valueFormatter: (params) => {
                    if (!params || !params.value)
                        return '';
                    const formatted = formatDMY(params.value);
                    return formatted === 'Invalid Date' ? '' : formatted;
                },
                renderCell: (params) => {
                    if (!params)
                        return '';
                    const formatted = formatDMY(params.value);
                    const formattedValue = formatted === 'Invalid Date' ? '' : formatted;
                    return (_jsx("span", { title: formattedValue, style: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }, children: formattedValue }));
                }
            });
        }
        // Add signed undertaking column if undertaking field exists
        const undertakingFieldId = getUndertakingFieldId();
        if (undertakingFieldId) {
            const undertakingFieldInResponseGrid = allResponsesGridLabelsByEventId?.find((label) => label.id === undertakingFieldId)?.isFieldInResponsesGrid;
            if (undertakingFieldInResponseGrid) {
                dynamicColumns.push({
                    field: 'signedUndertaking',
                    headerName: t('events.eventDetails.responsesTab.signedUndertaking', 'Signed Undertaking') ||
                        'Signed Undertaking',
                    minWidth: 180,
                    flex: 1,
                    sortable: false,
                    filterable: false,
                    renderCell: (params) => {
                        // Safety check for params
                        if (!params || !params.value) {
                            return (_jsx(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    width: '100%',
                                    py: 0.5
                                }, children: '' }));
                        }
                        const fileName = params.value || '';
                        const fileUrl = params.row.signedUndertakingUrl;
                        // Truncate filename to 11 characters maximum (excluding extension)
                        // If filename is longer than 11 chars, show first 11 chars + ...
                        let truncatedName = fileName;
                        if (fileName.length > 11) {
                            const nameWithoutExt = fileName.replace(/\.pdf$/i, '');
                            if (nameWithoutExt.length > 11) {
                                truncatedName = nameWithoutExt.substring(0, 11) + '...';
                            }
                            else {
                                truncatedName = fileName; // Keep full name if base name is <= 11 chars
                            }
                        }
                        const handleClick = (e) => {
                            e.stopPropagation();
                            if (fileUrl && fileName) {
                                handleDownloadSignedUndertaking(fileUrl, fileName);
                            }
                        };
                        return (_jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                py: 0.5,
                                cursor: fileUrl ? 'pointer' : 'default',
                                '&:hover': fileUrl
                                    ? {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        borderRadius: 1
                                    }
                                    : {}
                            }, title: fileName, onClick: handleClick, children: [_jsx(PdfIcon, { width: '20', height: '20' }), _jsx(Typography, { variant: 'body2', sx: {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                        minWidth: 0,
                                        color: fileUrl ? theme.palette.primary.main : 'inherit'
                                    }, children: truncatedName })] }));
                    },
                    valueFormatter: (params) => {
                        // Safety check for params
                        if (!params)
                            return '';
                        return params.value || '';
                    }
                });
            }
        }
        // Add actions column at the end only if there are dynamic columns
        if (dynamicColumns.length > 0) {
            dynamicColumns.push({
                field: 'responseLanguage',
                headerName: t('events.eventDetails.responsesTab.responseLanguage') || 'Response Language',
                minWidth: 180,
                sortable: false,
                filterable: false,
                renderCell: (params) => (_jsx(Box, { sx: { ...centered, height: '100%', gap: 1 }, children: (() => {
                        const lang = params.row.responseLanguage;
                        const src = lang?.flagUrl ?? '-';
                        const alt = lang?.name ?? '-';
                        const label = lang?.name ?? '-';
                        if (!lang) {
                            return (_jsx(Typography, { variant: 'body2', sx: { fontWeight: 500, color: theme.palette.grey[800] }, children: label }));
                        }
                        return (_jsxs(_Fragment, { children: [_jsx(Box, { component: 'img', src: src, alt: alt, sx: { width: 20, height: 15, objectFit: 'cover', borderRadius: 0.5 } }), _jsx(Typography, { variant: 'body2', sx: { fontWeight: 500, color: theme.palette.grey[800] }, children: label })] }));
                    })() }))
            }, {
                field: 'actions',
                headerName: 'Actions',
                width: ACTIONS_COLUMN_WIDTH,
                minWidth: ACTIONS_COLUMN_WIDTH,
                maxWidth: ACTIONS_COLUMN_WIDTH,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                filterable: false,
                renderCell: (params) => (_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                        gap: 1,
                        flexShrink: 0
                    }, children: [selectedTab === 'active' && (_jsx(OutlinedIconButton, { size: 'small', icon: isEditDialogLoading && selectedUserData?.id === params.row.id ? (_jsx(CircularProgress, { size: 16, color: 'inherit' })) : (_jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary })), onClick: () => handleView(params.row.id), disabled: !canEditEvent || isCollaborator })), _jsx(OutlinedIconButton, { size: 'small', icon: _jsx(Icon, { icon: 'eva:more-horizontal-fill', color: theme.palette.text.primary }), onClick: (event) => handleMenuOpen(event, params.row.id), disabled: isCollaborator })] }))
            });
        }
        return dynamicColumns;
    };
    // Transform API response data to grid rows
    const createDynamicRows = () => {
        const dataSource = selectedTab === 'active' ? allResponsesByEventId : allArchivedResponsesByEventId;
        if (!dataSource?.items || dataSource.items.length === 0) {
            return [];
        }
        // Process grouped responses by registration ID
        return dataSource.items.map((group) => {
            const row = {
                id: group.registrationId,
                // Use application default language when response language is missing
                responseLanguage: group.responseLanguage || null
            };
            // First, initialize all fields with undefined so formatters can handle them properly
            if (allResponsesGridLabelsByEventId) {
                allResponsesGridLabelsByEventId
                    .filter((label) => label.isFieldInResponsesGrid)
                    .forEach((label) => {
                    // Initialize with undefined so formatters can detect missing values
                    row[label.id] = undefined;
                });
            }
            // Extract responded date from responses (use earliest respondedAt)
            let respondedDate = undefined;
            if (group.responses && group.responses.length > 0) {
                const respondedDates = group.responses
                    .map((r) => r.respondedAt)
                    .filter((date) => date != null)
                    .map((date) => new Date(date));
                if (respondedDates.length > 0) {
                    // Get the earliest responded date
                    respondedDate = new Date(Math.min(...respondedDates.map((d) => d.getTime())));
                }
            }
            row.respondedDate = respondedDate;
            // Process each response in the group
            group.responses.forEach((response) => {
                if (response.fieldId) {
                    // If there are selected options, display them as comma-separated values
                    if (response.selectedOptions && response.selectedOptions.length > 0) {
                        const optionLabels = response.selectedOptions.map((option) => option.label);
                        row[response.fieldId] = optionLabels.join(', ');
                    }
                    else if (response.responseValue &&
                        response.responseValue !== '[]' &&
                        response.responseValue !== '') {
                        // Only set the field value if there's actual data (not empty array string or empty string)
                        // Try to parse JSON to check if it's an empty array
                        try {
                            const parsedValue = JSON.parse(response.responseValue);
                            if (Array.isArray(parsedValue) && parsedValue.length > 0) {
                                row[response.fieldId] = parsedValue.join(', ');
                            }
                            else if (!Array.isArray(parsedValue)) {
                                // Store raw value for grid formatting
                                row[response.fieldId] = response.responseValue;
                            }
                            // If it's an empty array, field remains undefined (formatters will handle it)
                        }
                        catch {
                            // If not JSON, use the value as is (but only if it's not empty)
                            if (response.responseValue.trim() !== '') {
                                // Store raw value for grid formatting
                                row[response.fieldId] = response.responseValue;
                            }
                            // If empty, field remains undefined (formatters will handle it)
                        }
                    }
                    // If no value or empty value, field remains undefined (formatters will handle it)
                }
            });
            // Add signed undertaking document name and URL if available
            const signedUndertakingDoc = findSignedUndertakingDocument(group.responses);
            if (signedUndertakingDoc) {
                row.signedUndertaking = signedUndertakingDoc.name;
                row.signedUndertakingUrl = signedUndertakingDoc.url;
            }
            else {
                row.signedUndertaking = undefined;
                row.signedUndertakingUrl = undefined;
            }
            return row;
        });
    };
    // Menu actions for each row
    const rowMenuActions = [
        {
            text: selectedTab === 'active'
                ? t('actions.archive', 'Archive')
                : t('actions.unarchive', 'UnArchive'),
            icon: selectedTab === 'active' ? (_jsx(Archive, { variant: 'Outline', style: { width: 20, height: 20 } })) : (_jsx(UnarchiveOutlined, { style: { width: 20, height: 20 } })),
            to: () => handleArchive(selectedRowId),
            disabled: !canEditEvent || isCollaborator,
            group: true
        },
        {
            text: t('actions.delete', 'Delete'),
            icon: _jsx(Trash, { variant: 'Outline', style: { width: 20, height: 20 } }),
            color: theme.palette.error.light,
            to: () => handleDelete(selectedRowId),
            disabled: !canDeleteEvent || isCollaborator,
            group: true
        }
    ];
    const handleMenuOpen = (event, rowId) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedRowId(rowId);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedRowId(null);
    };
    const handleView = (id) => {
        // Find the user data from dynamic rows
        const userData = createDynamicRows().find((row) => row.id === id);
        if (userData) {
            setSelectedUserData({
                id: userData.id,
                name: `Response ${userData.id}` // Use registration ID as name since we don't have firstName/lastName
            });
            setEditUserDialogOpen(true);
            setIsEditDialogLoading(true);
            // Fetch steps, fields, and responses for this registration
            dispatch(getStepsFieldsResponsesByRegistrationIdAsync({
                registrationId: id,
                eventId: eventId
            }));
        }
    };
    const handleFamilyFestView = (id) => {
        setSelectedUserData({
            id,
            name: `Attendee ${id}`
        });
        setIsEditDialogLoading(false);
        setEditUserDialogOpen(true);
    };
    const handleArchive = async (id) => {
        if (id) {
            setActionTargetId(id);
            setArchiveConfirmationOpen(true);
        }
    };
    const handleDelete = async (id) => {
        if (id) {
            setActionTargetId(id);
            setDeleteConfirmationOpen(true);
        }
    };
    const handleConfirmArchive = async () => {
        if (actionTargetId) {
            try {
                const request = {
                    registrationIds: [actionTargetId]
                };
                await dispatch(archiveResponsesAsync(request)).unwrap();
                // Show success toast
                toast.success(t('events.eventDetails.responsesTab.responseArchivedSuccessfully') ||
                    'Response archived successfully');
                // Refresh all data after successful archive to update counts
                refreshAllData();
                handleMenuClose();
                setArchiveConfirmationOpen(false);
                setActionTargetId(null);
            }
            catch (error) {
                // Show error toast
                const errorMessage = error?.message ||
                    error?.error ||
                    t('events.eventDetails.responsesTab.failedToArchiveResponse') ||
                    'Failed to archive response. Please try again.';
                toast.error(errorMessage);
                setArchiveConfirmationOpen(false);
                setActionTargetId(null);
            }
        }
    };
    const handleConfirmUnArchive = async () => {
        if (actionTargetId) {
            try {
                const request = {
                    registrationIds: [actionTargetId]
                };
                await dispatch(unarchiveResponsesAsync(request)).unwrap();
                // Show success toast
                toast.success(t('events.eventDetails.responsesTab.responseUnarchivedSuccessfully') ||
                    'Response unarchived successfully');
                // Refresh all data after successful unarchive to update counts
                refreshAllData();
                handleMenuClose();
                setArchiveConfirmationOpen(false);
                setActionTargetId(null);
            }
            catch (error) {
                // Show error toast
                const errorMessage = error?.message ||
                    error?.error ||
                    t('events.eventDetails.responsesTab.failedToUnarchiveResponse') ||
                    'Failed to unarchive response. Please try again.';
                toast.error(errorMessage);
                setArchiveConfirmationOpen(false);
                setActionTargetId(null);
            }
        }
    };
    const handleConfirmDelete = async () => {
        if (actionTargetId) {
            try {
                const request = {
                    registrationIds: [actionTargetId]
                };
                await dispatch(deleteResponsesAsync(request)).unwrap();
                // Show success toast
                toast.success(t('events.eventDetails.responsesTab.responseDeletedSuccessfully') ||
                    'Response deleted successfully');
                // Refresh all data after successful delete to update counts
                refreshAllData();
                handleMenuClose();
                setDeleteConfirmationOpen(false);
                setActionTargetId(null);
            }
            catch (error) {
                // Show error toast
                const errorMessage = error?.message ||
                    error?.error ||
                    t('events.eventDetails.responsesTab.failedToDeleteResponse') ||
                    'Failed to delete response. Please try again.';
                toast.error(errorMessage);
                setDeleteConfirmationOpen(false);
                setActionTargetId(null);
            }
        }
    };
    // Bulk operations
    const handleBulkDelete = async (selectedRows) => {
        if (selectedRows.length === 0) {
            toast.warning('Please select rows to delete');
            return;
        }
        setSelectedRowsForBulkOperation(selectedRows);
        setBulkDeleteConfirmationOpen(true);
    };
    const handleBulkArchive = async (selectedRows) => {
        if (selectedRows.length === 0) {
            toast.warning('Please select rows to archive');
            return;
        }
        setSelectedRowsForBulkOperation(selectedRows);
        setBulkArchiveConfirmationOpen(true);
    };
    const handleBulkUnarchive = async (selectedRows) => {
        if (selectedRows.length === 0) {
            toast.warning('Please select rows to unarchive');
            return;
        }
        setSelectedRowsForBulkOperation(selectedRows);
        setBulkUnarchiveConfirmationOpen(true);
    };
    // Actual bulk operation handlers called from confirmation dialogs
    const handleBulkDeleteConfirmed = async (selectedRows) => {
        try {
            const request = {
                registrationIds: selectedRows
            };
            await dispatch(deleteResponsesAsync(request)).unwrap();
            toast.success(t('events.eventDetails.responsesTab.bulkDeleteSuccess', { count: selectedRows.length }));
            refreshAllData();
        }
        catch (error) {
            const errorMessage = error?.message ||
                error?.error ||
                'Failed to delete responses. Please try again.';
            toast.error(errorMessage);
        }
    };
    const handleBulkArchiveConfirmed = async (selectedRows) => {
        try {
            const request = {
                registrationIds: selectedRows
            };
            await dispatch(archiveResponsesAsync(request)).unwrap();
            toast.success(t('events.eventDetails.responsesTab.bulkArchiveSuccess', { count: selectedRows.length }));
            refreshAllData();
        }
        catch (error) {
            const errorMessage = error?.message ||
                error?.error ||
                'Failed to archive responses. Please try again.';
            toast.error(errorMessage);
        }
    };
    const handleBulkUnarchiveConfirmed = async (selectedRows) => {
        try {
            const request = {
                registrationIds: selectedRows
            };
            await dispatch(unarchiveResponsesAsync(request)).unwrap();
            toast.success(t('events.eventDetails.responsesTab.bulkUnarchiveSuccess', { count: selectedRows.length }));
            refreshAllData();
        }
        catch (error) {
            const errorMessage = error?.message ||
                error?.error ||
                'Failed to unarchive responses. Please try again.';
            toast.error(errorMessage);
        }
    };
    const handleExportExcel = async () => {
        if (!eventId) {
            toast.error('Event ID not found');
            return;
        }
        try {
            // First get fresh field data for this specific event
            await dispatch(getAllResponsesGridLabelsByEventIdAsync(eventId)).unwrap();
            // Export based on selected tab
            const exportPromise = selectedTab === 'active'
                ? dispatch(exportResponsesToExcelAsync(eventId))
                : dispatch(exportResponsesToExcelAsync(eventId));
            const result = await exportPromise.unwrap();
            if (!result || result.length === 0) {
                toast.warning('No data available to export');
                return;
            }
            // Build field mapping from fresh grid labels data
            const allFieldLabels = {};
            const allFieldTypes = {};
            const fieldOrderMap = {};
            // Get fresh field info from the updated Redux state (grid labels)
            if (allResponsesGridLabelsByEventId) {
                allResponsesGridLabelsByEventId.forEach((field) => {
                    allFieldLabels[field.id] = field.label;
                    fieldOrderMap[field.id] = field.fieldSortOrderInResponseGrid || 0;
                });
            }
            // Also get field types from stepsFieldsResponses if available
            if (stepsFieldsResponses) {
                stepsFieldsResponses.forEach((step) => {
                    step.formFields?.forEach((field) => {
                        allFieldTypes[field.id] = field.fieldType;
                    });
                });
            }
            // Also collect field IDs from the actual response data to ensure we don't miss any
            const responseFieldIds = new Set();
            result.forEach((group) => {
                group.responses.forEach((response) => {
                    if (response.fieldId) {
                        responseFieldIds.add(response.fieldId);
                    }
                });
            });
            // Create ordered list of fields we have labels for
            const orderedFields = [];
            Object.keys(allFieldLabels).forEach((fieldId) => {
                const label = allFieldLabels[fieldId];
                if (label) {
                    orderedFields.push({
                        id: fieldId,
                        label: label,
                        fieldOrder: fieldOrderMap[fieldId] || 0
                    });
                }
            });
            // Sort fields by fieldOrder to maintain the same order as registration
            orderedFields.sort((a, b) => a.fieldOrder - b.fieldOrder);
            // Add signed undertaking column to ordered fields if undertaking field exists
            const undertakingFieldId = getUndertakingFieldId();
            if (undertakingFieldId) {
                orderedFields.push({
                    id: 'signedUndertaking',
                    label: t('events.eventDetails.responsesTab.signedUndertaking', 'Signed Undertaking') ||
                        'Signed Undertaking',
                    fieldOrder: 999999 // Place at the end
                });
            }
            // Ensure we have fields to export
            if (orderedFields.length === 0) {
                toast.error('No field definitions found. Cannot export to Excel.');
                return;
            }
            // Collect additional attendee sub-field labels from response data
            const additionalSubFieldLabelMap = {};
            const additionalAttendeeParentFieldIds = new Set();
            if (allResponsesGridLabelsByEventId) {
                allResponsesGridLabelsByEventId.forEach((f) => {
                    if (f.fieldType === FieldType.ADDITIONAL_ATTENDEES) {
                        additionalAttendeeParentFieldIds.add(f.id);
                    }
                });
            }
            result.forEach((group) => {
                group.responses.forEach((r) => {
                    if (r.isAdditionalAttendeeSubField && r.fieldId && r.fieldLabel) {
                        additionalSubFieldLabelMap[r.fieldId] = r.fieldLabel;
                    }
                });
            });
            // Helper to resolve a response value to display text
            const resolveResponseValue = (response) => {
                if (response.selectedOptions && response.selectedOptions.length > 0) {
                    return response.selectedOptions.map((o) => o.label).join(', ');
                }
                const rv = response.responseValue;
                if (!rv || rv === '[]' || rv === '') {
                    const ft = allFieldTypes[response.fieldId];
                    return ft === FieldType.TOGGLE ? 'No' : '';
                }
                try {
                    const parsed = JSON.parse(rv);
                    if (Array.isArray(parsed))
                        return parsed.length > 0 ? parsed.join(', ') : '';
                }
                catch { /* not JSON */ }
                const ft = allFieldTypes[response.fieldId];
                if (ft === FieldType.TOGGLE)
                    return formatToggleValue(rv);
                if (ft === FieldType.TIME)
                    return formatTime12Hour(rv);
                if (ft === FieldType.DATE)
                    return formatDMY(rv);
                return rv;
            };
            // Build final ordered field list:
            // - "Attendee Type" sentinel column first
            // - Main fields (skip AdditionalAttendees parent columns)
            // - Additional attendee sub-fields appended at end
            const ATTENDEE_TYPE_KEY = '__attendeeType';
            const mainOrderedFields = orderedFields.filter((f) => !additionalAttendeeParentFieldIds.has(f.id));
            const additionalSubFields = Object.entries(additionalSubFieldLabelMap).map(([id, label]) => ({
                id,
                label,
                fieldOrder: 999998
            }));
            const hasAdditionalAttendees = additionalSubFields.length > 0;
            const allOrderedFields = hasAdditionalAttendees
                ? [
                    { id: ATTENDEE_TYPE_KEY, label: t('events.eventDetails.responsesTab.attendeeType', { lng: primaryEventLanguageCode, defaultValue: 'Attendee Type' }), fieldOrder: -1 },
                    ...mainOrderedFields,
                    ...additionalSubFields
                ]
                : mainOrderedFields;
            // Transform the data for Excel export - use field IDs as keys internally
            const exportData = [];
            result.forEach((group) => {
                const mainRow = {};
                // Initialize columns
                allOrderedFields.forEach((field) => {
                    if (field.id === ATTENDEE_TYPE_KEY) {
                        mainRow[ATTENDEE_TYPE_KEY] = t('events.eventDetails.responsesTab.mainAttendee', { lng: primaryEventLanguageCode, defaultValue: 'Main Attendee' });
                        return;
                    }
                    const fieldType = allFieldTypes[field.id];
                    mainRow[field.id] = fieldType === FieldType.TOGGLE ? 'No' : '';
                });
                // Collect additional attendee responses grouped by instance number
                const additionalByInstance = {};
                // Process each response in the group
                group.responses.forEach((response) => {
                    if (!response.fieldId)
                        return;
                    if (response.isAdditionalAttendeeSubField) {
                        // Additional attendee sub-field — goes into a separate row
                        const instanceNum = response.additionalAttendeeInstanceNumber ?? 1;
                        if (!additionalByInstance[instanceNum]) {
                            additionalByInstance[instanceNum] = {};
                        }
                        additionalByInstance[instanceNum][response.fieldId] = resolveResponseValue(response);
                        return;
                    }
                    // Skip parent AdditionalAttendees field (no column for it)
                    if (additionalAttendeeParentFieldIds.has(response.fieldId))
                        return;
                    if (!allFieldLabels[response.fieldId])
                        return;
                    mainRow[response.fieldId] = resolveResponseValue(response);
                });
                // Add signed undertaking document name if available
                const signedUndertakingFileName = findSignedUndertakingDocument(group.responses);
                mainRow.signedUndertaking = signedUndertakingFileName ? signedUndertakingFileName : '';
                exportData.push(mainRow);
                // Append one row per additional attendee instance (sorted by instance number)
                Object.entries(additionalByInstance)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .forEach(([instanceNum, fields]) => {
                    const additionalRow = {};
                    allOrderedFields.forEach((field) => {
                        if (field.id === ATTENDEE_TYPE_KEY) {
                            additionalRow[ATTENDEE_TYPE_KEY] = t('events.eventDetails.responsesTab.additionalAttendeeLabel', { lng: primaryEventLanguageCode, number: Number(instanceNum) + 1, defaultValue: `Additional Attendee ${Number(instanceNum) + 1}` });
                            return;
                        }
                        const fieldType = allFieldTypes[field.id];
                        additionalRow[field.id] = fields[field.id] ?? (fieldType === FieldType.TOGGLE ? 'No' : '');
                    });
                    exportData.push(additionalRow);
                });
            });
            // Generate Excel file with proper column headers
            const ws = XLSX.utils.json_to_sheet(exportData);
            // Replace field ID headers with human-readable labels
            const headerRow = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']) : null;
            if (headerRow) {
                allOrderedFields.forEach((field, index) => {
                    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
                    if (ws[cellAddress]) {
                        ws[cellAddress].v = field.label;
                    }
                });
            }
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Responses');
            // Create filename with timestamp
            const now = new Date();
            const dateTime = now
                .toLocaleString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            })
                .replace(/[/:]/g, '-')
                .replace(',', '');
            const exportFileName = `event_responses-${dateTime}.xlsx`;
            XLSX.writeFile(wb, exportFileName);
            toast.success('Data exported successfully!');
        }
        catch (error) {
            toast.error('Failed to export data. Please try again.');
        }
    };
    const handleEditColumnDialog = () => {
        setEditColumnDialogOpen(true);
    };
    const handleCloseEditUserDialog = () => {
        setEditUserDialogOpen(false);
        setSelectedUserData(null);
    };
    const optionsMenuActions = [
        {
            text: t('events.eventDetails.responsesTab.editColumns', 'Edit Columns'),
            icon: _jsx(GridEdit, {}),
            group: true,
            to: () => {
                handleEditColumnDialog();
                handleOptionsMenuClose();
            },
            disabled: !canEditEvent
        },
        {
            text: t('common.exportToExcel', 'Export to Excel'),
            icon: _jsx(Export, { variant: 'Outline' }),
            group: true,
            to: () => {
                handleExportExcel();
                handleOptionsMenuClose();
            },
            disabled: false
        }
    ];
    const pinnedActionsColumnSx = useMemo(() => {
        const pinnedCellShadow = '-4px 0 8px -4px rgba(0, 0, 0, 0.12)';
        const pinnedHeaderShadow = '-4px 0 8px -4px rgba(0, 0, 0, 0.08)';
        const baseBg = theme.palette.common.white;
        const headerBg = getOpaqueColorMix(baseBg, theme.palette.primary.main, 0.08);
        const cellBg = baseBg;
        const rowHoverBg = getOpaqueColorMix(baseBg, theme.palette.primary.main, theme.palette.action.hoverOpacity);
        const rowSelectedBg = getOpaqueColorMix(baseBg, theme.palette.primary.main, theme.palette.action.selectedOpacity);
        const pinnedDivider = `1px solid ${alpha(theme.palette.grey[500], 0.24)}`;
        return {
            '& .MuiDataGrid-columnHeaders': {
                zIndex: 3
            },
            '& .MuiDataGrid-columnHeader:not([data-field="actions"])': {
                zIndex: 1
            },
            '& .MuiDataGrid-virtualScroller': {
                zIndex: 1
            },
            '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                position: 'sticky',
                right: 0,
                zIndex: 12,
                isolation: 'isolate',
                backgroundColor: `${headerBg} !important`,
                boxShadow: pinnedHeaderShadow,
                borderLeft: pinnedDivider,
                overflow: 'hidden',
                padding: '0 8px !important',
                ...pinnedColumnSize(ACTIONS_COLUMN_WIDTH),
                ...pinnedHeaderChildrenBg(headerBg)
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
                position: 'sticky',
                right: 0,
                zIndex: 4,
                isolation: 'isolate',
                backgroundColor: `${cellBg} !important`,
                boxShadow: pinnedCellShadow,
                borderLeft: pinnedDivider,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 8px !important',
                ...pinnedColumnSize(ACTIONS_COLUMN_WIDTH)
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="actions"]': {
                backgroundColor: `${rowHoverBg} !important`
            },
            '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field="actions"]': {
                backgroundColor: `${rowSelectedBg} !important`
            },
            '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell[data-field="actions"]': {
                backgroundColor: `${rowSelectedBg} !important`
            }
        };
    }, [theme]);
    // Removed mockRows, MockFamilyRow, and AddFamilyResponseDialog. Only use real API data.
    return (_jsxs(_Fragment, { children: [IsFamilyFest ? (_jsx(FamilyResponsesGrid, { refetchAttendeesRef: familyFestRefetchAttendeesRef, eventId: eventId, onOpenAddDialog: () => setAddAttendeeDialogOpen(true), onEditRow: handleFamilyFestView, isEditDialogLoading: isEditDialogLoading, selectedUserDataId: selectedUserData?.id, onAttendeesChanged: onAttendeesChanged })) : (_jsxs(_Fragment, { children: [_jsx(Box, { children: _jsx(PrimaryGrid, { disableVirtualization: true, sx: [
                                { height: { xs: 'calc(100dvh - 120px)', sm: 'calc(100dvh - 180px)' } },
                                pinnedActionsColumnSx
                            ], rows: createDynamicRows(), showTabs: true, archivebutton: true, selectedButton: selectedTab, setSelectedButton: setSelectedTab, columns: createDynamicColumns(), paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: 'server', sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                                setSortModel(model);
                                setPaginationModel((prev) => ({ ...prev, page: 0 }));
                            }, showExportExcelButton: false, headerRightControles: selectedTab === 'active' && (_jsxs(Box, { sx: { display: 'flex', gap: 1.5, alignItems: 'center' }, children: [_jsx(Button, { startIcon: _jsx(Icon, { icon: 'eva:more-horizontal-fill' }), size: isMobile ? 'medium' : 'large', variant: 'outlined', disabled: isCollaborator, onClick: handleOptionsMenuOpen, color: 'primary', children: t('common.options') }), _jsx(Button, { startIcon: _jsx(AddCircle, { variant: 'Bold', size: 20 }), size: isMobile ? 'medium' : 'large', variant: 'contained', disabled: isCollaborator || !canEditEvent, onClick: () => {
                                            if (!canEditEvent) {
                                                toast.error(t('common.unauthorized') ||
                                                    'You do not have permission to add attendees');
                                                return;
                                            }
                                            setAddAttendeeDialogOpen(true);
                                        }, children: t('events.eventDetails.responsesTab.addAttendee') }), Boolean(optionsMenuAnchorEl) && (_jsx(StyledMenu, { anchorEl: optionsMenuAnchorEl, open: Boolean(optionsMenuAnchorEl), onClose: handleOptionsMenuClose, actions: optionsMenuActions }))] })), showInvitationsAccessButton: false, onExportExcelClick: handleExportExcel, showFilters: false, noRowsText: t('events.eventDetails.responsesTab.noResponses'), checkboxSelection: !isCollaborator, disableRowSelectionOnClick: true, pageSizeOptions: [5, 10, 25, 50], rowCount: selectedTab === 'active'
                                ? allResponsesByEventId?.totalCount || 0
                                : allArchivedResponsesByEventId?.totalCount || 0, onBulkArchive: isCollaborator ? undefined : handleBulkArchive, onBulkUnarchive: isCollaborator ? undefined : handleBulkUnarchive, onBulkDelete: isCollaborator ? undefined : handleBulkDelete }) }), Boolean(menuAnchorEl) && (_jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, actions: rowMenuActions }))] })), !IsFamilyFest && (_jsxs(_Fragment, { children: [_jsx(EditColumnDialog, { open: editColumnDialogOpen, onClose: () => setEditColumnDialogOpen(false), onRefreshGridData: refreshGridData }), _jsx(ConfirmationDialog, { open: archiveConfirmationOpen, onClose: () => {
                            setArchiveConfirmationOpen(false);
                            setActionTargetId(null);
                        }, title: selectedTab === 'active'
                            ? t('common.archiveConfirmation')
                            : t('common.unarchiveConfirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, size: 'large', variant: 'outlined', color: 'inherit', onClick: () => {
                                        setArchiveConfirmationOpen(false);
                                        setActionTargetId(null);
                                    }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, onClick: selectedTab === 'active' ? handleConfirmArchive : handleConfirmUnArchive, variant: 'contained', color: 'inherit', size: 'large', disabled: !canEditEvent || isCollaborator, children: selectedTab === 'active' ? t('actions.archive') : t('actions.unarchive') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: selectedTab === 'active'
                                    ? t('events.eventDetails.responsesTab.confirmArchiveText')
                                    : t('events.eventDetails.responsesTab.confirmUnArchiveText') ||
                                        'Are you sure you want to archive this response?' }) }) }), _jsx(ConfirmationDialog, { open: deleteConfirmationOpen, onClose: () => {
                            setDeleteConfirmationOpen(false);
                            setActionTargetId(null);
                        }, title: t('common.deleteConfirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', color: 'inherit', fullWidth: true, variant: 'outlined', onClick: () => {
                                        setDeleteConfirmationOpen(false);
                                        setActionTargetId(null);
                                    }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'error', fullWidth: true, disabled: !canDeleteEvent || isCollaborator, onClick: handleConfirmDelete, children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.confirmDeleteText') ||
                                    'Are you sure you want to delete this response? This action cannot be undone.' }) }) }), _jsx(ConfirmationDialog, { open: bulkDeleteConfirmationOpen, onClose: () => {
                            setBulkDeleteConfirmationOpen(false);
                            setSelectedRowsForBulkOperation([]);
                        }, title: t('events.eventDetails.responsesTab.bulkDeleteConfirmationTitle') ||
                            'Confirm Bulk Delete', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                        setBulkDeleteConfirmationOpen(false);
                                        setSelectedRowsForBulkOperation([]);
                                    }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', fullWidth: true, onClick: () => {
                                        handleBulkDeleteConfirmed(selectedRowsForBulkOperation);
                                        setBulkDeleteConfirmationOpen(false);
                                        setSelectedRowsForBulkOperation([]);
                                    }, children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.bulkDeleteConfirmationText', {
                                    count: selectedRowsForBulkOperation.length
                                }) ||
                                    `Are you sure you want to delete ${selectedRowsForBulkOperation.length} selected response(s)? This action cannot be undone.` }) }) }), _jsx(ConfirmationDialog, { open: bulkArchiveConfirmationOpen, onClose: () => {
                            setBulkArchiveConfirmationOpen(false);
                            setSelectedRowsForBulkOperation([]);
                        }, title: t('events.eventDetails.responsesTab.bulkArchiveConfirmationTitle') ||
                            'Confirm Bulk Archive', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                        setBulkArchiveConfirmationOpen(false);
                                        setSelectedRowsForBulkOperation([]);
                                    }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', fullWidth: true, variant: 'contained', color: 'inherit', disabled: !canEditEvent || isCollaborator, onClick: () => {
                                        handleBulkArchiveConfirmed(selectedRowsForBulkOperation);
                                        setBulkArchiveConfirmationOpen(false);
                                        setSelectedRowsForBulkOperation([]);
                                    }, children: t('actions.archive') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.bulkArchiveConfirmationText', {
                                    count: selectedRowsForBulkOperation.length
                                }) ||
                                    `Are you sure you want to archive ${selectedRowsForBulkOperation.length} selected response(s)?` }) }) }), _jsx(ConfirmationDialog, { open: bulkUnarchiveConfirmationOpen, onClose: () => {
                            setBulkUnarchiveConfirmationOpen(false);
                            setSelectedRowsForBulkOperation([]);
                        }, title: t('events.eventDetails.responsesTab.bulkUnarchiveConfirmationTitle') ||
                            'Confirm Bulk Unarchive', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', size: 'large', onClick: () => {
                                        setBulkUnarchiveConfirmationOpen(false);
                                        setSelectedRowsForBulkOperation([]);
                                    }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'inherit', fullWidth: true, disabled: !canEditEvent || isCollaborator, onClick: () => {
                                        handleBulkUnarchiveConfirmed(selectedRowsForBulkOperation);
                                        setBulkUnarchiveConfirmationOpen(false);
                                        setSelectedRowsForBulkOperation([]);
                                    }, children: t('actions.unarchive') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: (() => {
                                    const count = selectedRowsForBulkOperation.length;
                                    return (t('events.eventDetails.responsesTab.bulkUnarchiveConfirmationText', {
                                        count
                                    }) || `Are you sure you want to unarchive ${count} selected response(s)?`);
                                })() }) }) })] })), editUserDialogOpen &&
                (IsFamilyFest ? (_jsx(EditFamilyResponseDialog, { open: editUserDialogOpen, onClose: handleCloseEditUserDialog, userId: selectedUserData?.id, userName: selectedUserData?.name, eventId: eventId, paginationModel: paginationModel, onRefreshAttendees: () => {
                        familyFestRefetchAttendeesRef.current?.();
                        onAttendeesChanged?.();
                    } })) : (_jsx(EditUserResponseDialog, { open: editUserDialogOpen, onClose: handleCloseEditUserDialog, userId: selectedUserData?.id, userName: selectedUserData?.name, eventId: eventId, paginationModel: paginationModel }))), IsFamilyFest ? (_jsx(AddFamilyResponseDialog, { open: addAttendeeDialogOpen, onClose: () => setAddAttendeeDialogOpen(false), eventId: eventId || '', paginationModel: paginationModel, onRefreshAttendees: () => familyFestRefetchAttendeesRef.current?.() })) : (_jsx(AddUserResponseDialog, { open: addAttendeeDialogOpen, onClose: () => setAddAttendeeDialogOpen(false), eventId: eventId || '', paginationModel: paginationModel }))] }));
};
